import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FaUndo } from 'react-icons/fa';

import { FormattedMessage, injectIntl } from 'react-intl';
import classnames from 'classnames';
import moment from 'moment';
import momentPropTypes from 'react-moment-proptypes';
import Toggle from 'react-toggle';

import intlShape from '@/shared/utils/intlShape';
import SimpleDatePicker from '@/shared/components/input/SimpleDatePicker';
import { hasMultipleCompanies } from '@/shared/components/security/HasMultiCompany';
import CompanyMultiSelect from '@/shared/components/input/multiselect/CompanyMultiSelect';
import TeamMultiSelect from '@/shared/components/input/multiselect/TeamMultiSelect';
import UserSearch from '@/shared/components/input/UserSearch';
import ResourceTypeButtonGroup from '@/shared/components/ResourceTypeButtonGroup';
import AuthService from '@/shared/services/AuthService';
import EmployeeTagMultiSelect from '@/modules/hrm/EmployeeTagMultiSelect';

import { isBeforeMinimum, isOutRange } from '@/shared/utils/validationHelpers';
import EnumSelectV2 from '@/shared/components/input/EnumSelectV2';
import Periods from './HoursPerEmployeeReportPeriods';

import messages from '../reportingMessages';
import dateFormatService from '../../../shared/services/DateFormatService';

const getMaxDateUntil = (groupBy, dateFrom) => {
  switch (groupBy) {
    case 1:
    case 2:
      return moment
        .utc(dateFrom)
        .add(1, 'month')
        .subtract(1, 'day');
    case 3:
      return moment
        .utc(dateFrom)
        .add(1, 'year')
        .subtract(1, 'day');
    case 4:
      return moment
        .utc(dateFrom)
        .add(20, 'year')
        .subtract(1, 'day');
    default:
      return null;
  }
};
// https://github.com/airbnb/react-dates/issues/239#issuecomment-458297507

export const defaultFilters = {
  groupBy: 1,
  dateFrom: moment.utc().startOf('month'),
  dateUntil: null,
  ownCompanies: [],
  userId: null,
  resourceTypes: [],
  teamCodes: [],
  employeeType: null,
  onlyWithMissingHours: false,
  withEmployeeLabels: [],
};

const groupByButtons = [
  { key: 1, label: messages.GroupByDay },
  { key: 2, label: messages.GroupByWeek },
  { key: 3, label: messages.GroupByMonth },
  { key: 4, label: messages.GroupByYear },
];

class HoursPerEmployeeReportFilters extends Component {
  static propTypes = {
    onFilterChange: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      groupBy: PropTypes.number,
      dateFrom: momentPropTypes.momentObj,
      dateUntil: momentPropTypes.momentObj,
      ownCompanies: PropTypes.arrayOf(PropTypes.string),
      resourceTypes: PropTypes.arrayOf(PropTypes.number),
      teamCodes: PropTypes.arrayOf(PropTypes.number),
      userId: PropTypes.string,
      employeeType: PropTypes.number,
      onlyWithMissingHours: PropTypes.bool,
      withEmployeeLabels: PropTypes.arrayOf(PropTypes.string),
    }),
    intl: intlShape.isRequired,
  };
  static defaultProps = {
    filters: defaultFilters,
  };
  state = {
    showUsers: false,
  };
  constructor(props) {
    super(props);
    this.state.showUsers = AuthService.hasPermission({
      functionality: 'RegistrationOthers',
      privilege: 'Read',
    });
  }

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };
    if (newFilters.dateFrom && newFilters.dateUntil) {
      newFilters.dateUntil = moment.min(
        newFilters.dateUntil,
        getMaxDateUntil(newFilters.groupBy, newFilters.dateFrom),
      );
    }

    if (JSON.stringify(filters) !== JSON.stringify(newFilters)) onFilterChange(newFilters);
  };
  dateChanged = (dateFrom, dateUntil) => {
    this.filterChanged({ dateFrom, dateUntil });
  };

  fromDateChanged = dateFrom => {
    this.filterChanged({ dateFrom });
  };
  untilDateChanged = dateUntil => {
    this.filterChanged({ dateUntil });
  };
  groupByChanged = group => {
    const { filters } = this.props;
    const newFilters = { groupBy: group };

    switch (group) {
      case 4: {
        newFilters.dateFrom = moment
          .utc(filters.dateFrom)
          .startOf('year')
          .subtract(5, 'year');
        newFilters.dateUntil = moment.utc().endOf('year');
        break;
      }
      case 3: {
        if (filters.groupBy === 4) {
          newFilters.dateFrom = moment.utc().startOf('year');
          newFilters.dateUntil = moment.utc().endOf('year');
        } else {
          newFilters.dateUntil = moment
            .utc(filters.dateFrom)
            .add(1, 'year')
            .subtract(1, 'day');
        }
        break;
      }
      case 1:
      case 2: {
        if (filters.groupBy > 2) {
          newFilters.dateFrom = moment.utc().startOf('month');
          newFilters.dateUntil = moment.utc().endOf('month');
        }
        break;
      }
      default:
        break;
    }

    this.filterChanged(newFilters);
  };
  resetFilters = () => {
    this.filterChanged({ ...defaultFilters });
  };
  userChanged = item => {
    this.filterChanged({
      userId: (item && item.id) || null,
    });
  };

  render() {
    const { filters, intl } = this.props;
    const { showUsers } = this.state;

    const showCompanies = hasMultipleCompanies();

    const hasFilters = JSON.stringify(filters) !== JSON.stringify(defaultFilters);
    const maxDateUntil = getMaxDateUntil(filters.groupBy, filters.dateFrom);

    return (
      <div>
        <div className="row">
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <label className="control-label">
                <FormattedMessage {...messages.GroupBy} />
              </label>
              <div className="btn-group">
                {groupByButtons.map(({ key, label }) => (
                  <button
                    key={key}
                    className={classnames('btn btn-default', { active: filters.groupBy === key })}
                    onClick={() => this.groupByChanged(key)}
                    type="button"
                  >
                    {intl.formatMessage(label)}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <label className="control-label margin10R">
                <FormattedMessage {...messages.DateFrom} />
              </label>
              <SimpleDatePicker
                placeholder={intl.formatMessage(messages.DateFrom)}
                date={filters.dateFrom}
                onDateChange={this.fromDateChanged}
                id="reportDateFrom"
                required
                isOutsideRange={isBeforeMinimum}
                displayFormat={dateFormatService.dateFormat()}
              />
            </div>
            <div className="form-group">
              <label className="control-label margin10R">
                <FormattedMessage {...messages.DateUntil} />
              </label>
              <SimpleDatePicker
                placeholder={intl.formatMessage(messages.DateUntil)}
                date={filters.dateUntil}
                onDateChange={this.untilDateChanged}
                id="reportDateUntil"
                isOutsideRange={d => isOutRange(d, filters.dateFrom, maxDateUntil)}
                displayFormat={dateFormatService.dateFormat()}
              />
            </div>
          </div>
          <div className="col-lg-6 col-md-12">
            <Periods filters={filters} onChangeDate={this.dateChanged} />
          </div>
        </div>

        <div className="row">
          {showUsers && (
            <div className="col-lg-3 col-md-6">
              <div className="form-group">
                <UserSearch
                  onChange={this.userChanged}
                  value={filters.userId}
                  placeholder={intl.formatMessage(messages.AllUsers)}
                />
              </div>
            </div>
          )}
          {showCompanies && (
            <div className="col-lg-3 col-md-6">
              <div className="form-group">
                <CompanyMultiSelect
                  selection={filters.ownCompanies}
                  placeholder={intl.formatMessage(messages.OwnCompanyEmployees)}
                  onChange={companies => this.filterChanged({ ownCompanies: companies })}
                />
              </div>
            </div>
          )}
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <ResourceTypeButtonGroup
                selectedTypes={filters.resourceTypes}
                allowedTypes={[0, 3, 4]}
                onChange={value => {
                  this.filterChanged({ resourceTypes: value });
                }}
              />
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <TeamMultiSelect
                selection={filters.teamCodes}
                onChange={value => this.filterChanged({ teamCodes: value })}
                idProp="code"
              />
            </div>
          </div>
          {showUsers && (
            <>
              <div className="col-lg-3 col-md-6">
                <div className="form-group">
                  <EnumSelectV2
                    enumType="employeeType"
                    value={filters.employeeType}
                    allowNull
                    placeholder={intl.formatMessage(messages.EmployeeType)}
                    onChange={value => {
                      this.filterChanged({ employeeType: value });
                    }}
                  />
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="form-group">
                  <EmployeeTagMultiSelect
                    value={filters.withEmployeeLabels}
                    onChange={withEmployeeLabels => this.filterChanged({ withEmployeeLabels })}
                    placeholder={intl.formatMessage(messages.WithEmployeeTags)}
                  />
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <label className="react-toggle-wrapper margin5T">
                  <Toggle
                    checked={filters.onlyWithMissingHours}
                    onChange={e => this.filterChanged({ onlyWithMissingHours: e.target.checked })}
                  />
                  <FormattedMessage
                    className="label-text"
                    {...messages.OnlyEmployeesWithMissingHours}
                  />
                </label>
              </div>
            </>
          )}
        </div>
        <div className="flex-row">
          <div className="btn-group save-filters">
            {hasFilters && (
              <button
                type="button"
                className="btn btn-default btn-reset"
                onClick={this.resetFilters}
              >
                <FaUndo className="react-icon" />
                &nbsp;
                <FormattedMessage {...messages.ResetFilters} />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default injectIntl(HoursPerEmployeeReportFilters);
